// ─────────────────────────────────────────────────────────────────────────────
// Reporting metric contract — single source of truth.
//
// Every analytics metric must declare HOW it is counted, so the visible cell
// count and its drill-down popup can't silently diverge (the bug class behind
// PRs #3535 / #3538 / #3544). The two axes that matter:
//
//   unit   — the population UNIT the metric counts:
//            'lead'                 → one row per LEAD (treatment-agnostic).
//            'treatment-transition' → one row per matching lead_treatment.
//   basis  — the timestamp that defines the period:
//            'created_at' | 'state_transition' | 'archived_at'.
//   boundary — how the selected UI date range becomes query params. Everything is
//            'dayBounds' (start-of-day → end-of-day). The aggregate COUNT and the
//            drilldown popup MUST use the same window — a "now minus X days" rolling
//            window (exact time) vs day bounds makes cell != popup flicker with the
//            time of day (the #3535 88-vs-93 class). Resolve the range ONCE here.
//   duplicates — whether duplicate leads count. Everything is 'included': the real
//            production behaviour (backend ignores is_duplicate on BOTH the reports
//            aggregate AND /leads/, so cells and popups already include dupes; the old
//            FE is_duplicate=false params were dead no-ops). Excluding dupes is a future
//            backend/product project (would change historical numbers 1–15% by practice).
//
// Enquiries/NEW is LEAD-level on created_at → treatment-less leads still count
// (a new enquiry often has no treatment yet). Status metrics are
// treatment-transition on state_transition → a treatment-less lead can't have
// transitioned, so it must NOT appear. Lost Leads is lead-level on archived_at.
// ─────────────────────────────────────────────────────────────────────────────

import dayjs from 'dayjs';

export const METRIC_UNIT = {
    LEAD: 'lead',
    TRANSITION: 'treatment-transition',
};

export const METRIC_BASIS = {
    CREATED_AT: 'created_at',
    STATE_TRANSITION: 'state_transition',
    ARCHIVED_AT: 'archived_at',
    // Dated by the consultation APPOINTMENT's datetime and final status, not by
    // the treatment's StateLog transition (spec 2026-10-21 §3.1; the Windsor
    // "attended in July, counted in October" defect). Served by
    // reports/appointment-stages/ — see src/utils/appointmentStages.js.
    APPOINTMENT: 'appointment',
};

export const METRIC_BOUNDARY = {
    DAY: 'dayBounds', // start-of-day → end-of-day (calendar window)
};

export const METRIC_DUPLICATES = {
    INCLUDED: 'included', // duplicate leads count (current prod behaviour; backend ignores is_duplicate)
};

// Keyed by lead status (the value the popup/aggregate use for `status`).
// Every metric currently uses dayBounds + duplicates:'included' (uniform), declared
// per-spec so getMetricSpec(status).{boundary,duplicates} reads like the other axes.
const D = METRIC_BOUNDARY.DAY;
const DUP = METRIC_DUPLICATES.INCLUDED;
export const METRIC_SPECS = {
    NEW: { unit: METRIC_UNIT.LEAD, basis: METRIC_BASIS.CREATED_AT, boundary: D, duplicates: DUP, label: 'New Enquiries' },
    CONSULTATION_BOOKED: { unit: METRIC_UNIT.TRANSITION, basis: METRIC_BASIS.STATE_TRANSITION, boundary: D, duplicates: DUP, label: 'Consult Booked' },
    CONSULTATION_ATTENDED: { unit: METRIC_UNIT.TRANSITION, basis: METRIC_BASIS.STATE_TRANSITION, boundary: D, duplicates: DUP, label: 'Attended' },
    TREATMENT_STARTED: { unit: METRIC_UNIT.TRANSITION, basis: METRIC_BASIS.STATE_TRANSITION, boundary: D, duplicates: DUP, label: 'Started' },
    CLOSED: { unit: METRIC_UNIT.TRANSITION, basis: METRIC_BASIS.STATE_TRANSITION, boundary: D, duplicates: DUP, label: 'Completed Treatment' },
    FAILED_TO_ATTEND_CONSULTATION: { unit: METRIC_UNIT.TRANSITION, basis: METRIC_BASIS.STATE_TRANSITION, boundary: D, duplicates: DUP, label: 'Failed to Attend' },
    ARCHIVED: { unit: METRIC_UNIT.LEAD, basis: METRIC_BASIS.ARCHIVED_AT, boundary: D, duplicates: DUP, label: 'Lost Leads' },
};

// Default for an unknown status is the CONSERVATIVE one: treatment-transition.
// (Never silently treat an unexpected status as lead-level — that would re-introduce
// treatment-less leads into a status drilldown.)
const DEFAULT_SPEC = { unit: METRIC_UNIT.TRANSITION, basis: METRIC_BASIS.STATE_TRANSITION, boundary: D, duplicates: DUP, label: '' };

export const getMetricSpec = (status) => METRIC_SPECS[status] || DEFAULT_SPEC;

export const isLeadUnit = (status) => getMetricSpec(status).unit === METRIC_UNIT.LEAD;

/**
 * Decide which lead_treatment represents `lead` for `status`, and whether the
 * lead emits a popup row at all — per the metric's population unit. Pure; used by
 * TreatmentLeadPopup for the no-explicit-treatment-scope case so the drilldown
 * population matches the aggregate count by construction.
 *
 *  - lead-unit (NEW / ARCHIVED): always emit ONE row; a treatment-less lead gets a
 *    synthetic empty match ({}) so it still renders ("No treatment").
 *  - transition-unit (status): emit only if the lead has a treatment — preferring
 *    one that entered `status` in range; a treatment-less lead is dropped.
 *
 * @returns {{ emit: boolean, matchLt: object|undefined }}
 */
export function selectMetricTreatment(lead, status, { hasTransitionInRange } = {}) {
    const lts = (lead && lead.lead_treatments) || [];
    if (isLeadUnit(status)) {
        return { emit: true, matchLt: lts[0] || {} };
    }
    const matched = typeof hasTransitionInRange === 'function'
        ? lts.find((lt) => hasTransitionInRange(lt, status))
        : undefined;
    const matchLt = matched || (lts.length ? lts[0] : undefined);
    return { emit: Boolean(matchLt), matchLt };
}

// ── Date-window resolution (boundary axis) ───────────────────────────────────
// All report metrics use day-bounds. These turn the UI date range (Date objects
// that may carry a current "now" time) into explicit start-of-day / end-of-day
// query strings, so the aggregate COUNT and the drilldown popup query the SAME
// window. Use these everywhere a created_at / state_transition / archived_at
// window is built — never `format('YYYY-MM-DD HH:mm:ss')` on the raw Date (that
// captures the current time and produces a rolling window that disagrees with
// the popup's day-bounds).
export const DATE_PARAM_FORMAT = 'YYYY-MM-DD HH:mm:ss';
export const reportDayStart = (date) => (date ? dayjs(date).startOf('day').format(DATE_PARAM_FORMAT) : '');
export const reportDayEnd = (date) => (date ? dayjs(date).endOf('day').format(DATE_PARAM_FORMAT) : '');
export const resolveReportDateWindow = (dateRange) => ({
    start: reportDayStart(dateRange && dateRange.startDate),
    end: reportDayEnd(dateRange && dateRange.endDate),
});
